// Every published price lives here, so the pages can't drift apart.
// Mirrors alan-worksheets business/price-sheet.md (confirmed by Alan
// 2026-09-30). Change a price there first, then here. leads.rare-works.com
// charges the same list prices (rareworks-leads supabase/functions/_shared).

export type PriceRow = { name: string; price: string; desc?: string };

export const FREE_MONTH =
  "Every monthly plan starts with the first 30 days free. One-time fees are paid up front.";

export const WEBSITE_PRICES: PriceRow[] = [
  {
    name: "Starter site (1 page)",
    price: "$400 once + a monthly plan",
    desc: "One page with your hours, phone, services or menu, photos, and a map. The site is yours.",
  },
  {
    name: "Starter site, managed",
    price: "$75/mo, no build fee",
    desc: "We build it, host it, and make your changes, up to 2 hours a month. Buy it from us any time for $400.",
  },
  {
    name: "Business site (up to 10 pages)",
    price: "$1,800 once + a care plan",
    desc: "A page for each service and town, SEO setup, and forms that email you. The site is yours.",
  },
  {
    name: "Business site, managed",
    price: "$150/mo, no build fee",
    desc: "12-month minimum. Includes Standard care. Buy it from us any time for $1,800.",
  },
  {
    name: "Extra pages",
    price: "$150 each",
    desc: "Sites over 10 pages are quoted on scope, using this as the guide.",
  },
  {
    name: "Custom builds",
    price: "Quoted",
    desc: "Booking, customer portals, and apps. Estimated at $85 an hour, 50% deposit, 50% at launch.",
  },
];

// What the hosting on every plan actually is: Porkbun Static Hosting, Starter tier (what we pay $3 a month
// per site for; checked on porkbun.com 2026-10-08). We sell this one plan. Anything bigger is a conversation.
// FTP: one login per hosting plan, and a plan belongs to one domain. A site on a free <name>.rare-works.com
// address is a folder inside OUR hosting, so it can never get its own FTP login.
export const HOSTING_SPECS: string[] = [
  "2 GB of storage for your site's files",
  "Unmetered bandwidth: no cap on visitors or traffic, within normal fair use",
  "Free SSL certificate, so the padlock shows in the browser",
  "Room for up to 10 subdomains on your domain",
  "Hosting only runs on your own domain name: bring one you have, transfer it to us, or we register one for you at cost",
  "On Hosting only you get your own FTP login to change your files (up to 40 MB per file)",
  "Built for static websites like the ones we make. No WordPress, databases, or email inboxes.",
  "Need more room or something this doesn't cover? Talk to us and we'll build it.",
];

export const CARE_PLANS: PriceRow[] = [
  {
    name: "Hosting only",
    price: "$5/mo or $50/yr",
    desc: "2 GB of storage, unmetered bandwidth, and a free SSL certificate for a site you own, on your own domain name. You get an FTP login to make your own changes. Our help is $75 an hour.",
  },
  {
    name: "Basic care",
    price: "$50/mo",
    desc: "Hosting, security, backups, and up to 1 hour of edits a month.",
  },
  {
    name: "Standard care",
    price: "$100/mo",
    desc: "Basic, plus up to 2 hours of edits and 2 Google Business Profile posts a month.",
  },
  {
    name: "Growth care",
    price: "$200/mo",
    desc: "Standard, plus up to 4 hours of edits, Google Business Profile management, local SEO, keyword tracking, and a monthly report.",
  },
];

export const SEO_PRICES: PriceRow[] = [
  {
    name: "Google Business Profile + local SEO setup",
    price: "$300 once",
    desc: "Category, services, products, photos, description, and fixes to what's already there.",
  },
  {
    name: "Google Business Profile + local SEO management",
    price: "$150/mo",
    desc: "Posts, updates, keyword tracking, and a monthly report. Included in Growth care.",
  },
];

export const SOCIAL_TIERS = [
  {
    name: "Starter",
    price: "$400/mo",
    desc: "Regular posts on two platforms, made and scheduled for you.",
    features: [
      "8 to 12 posts a month",
      "2 platforms (Facebook + Instagram)",
      "Custom graphics for each post",
      "Captions written for you",
      "Scheduled and posted for you",
    ],
  },
  {
    name: "Standard",
    price: "$700/mo",
    desc: "More posts, a third platform, and someone answering your comments.",
    features: [
      "16 to 20 posts a month",
      "3 platforms (Facebook, Instagram, TikTok)",
      "Stories",
      "Replies to comments",
      "Everything in Starter",
    ],
  },
  {
    name: "Full",
    price: "$1,000/mo",
    desc: "Standard, plus we run your ad campaigns.",
    features: [
      "Everything in Standard",
      "Ad campaign management",
      "Ad spend is paid by you, straight to the platform",
    ],
  },
];

// Where a site we host lives. Free: leads.rare-works.com/<name>/. Paid: a short address on one of our
// domains (Alan, 2026-10-08), or the customer's own domain at cost.
export const ADDRESS_PRICES: PriceRow[] = [
  {
    name: "Free address",
    price: "$0",
    desc: "Every site we host has one: leads.rare-works.com/yourname. It works on day one and costs nothing.",
  },
  {
    name: "yourname.rare-works.com",
    price: "$3.99/yr",
    desc: "A short RareWorks address for your site, paid by the year. It is ours, so it stops if you do not renew. Not refundable.",
  },
  {
    name: "yourname.rare-works.net",
    price: "$2.99/yr",
    desc: "The same thing on our .net.",
  },
  {
    name: "yourname.rare-works.xyz",
    price: "$1.99/yr",
    desc: "The same thing on our .xyz.",
  },
  {
    name: "Your own domain name",
    price: "At cost",
    desc: "Like yourbusiness.com, about $11 a year for a .com. No markup, and you own it. Not refundable once registered. Required for Hosting only.",
  },
];

export const DESIGN_PRICES: Record<string, string> = {
  "Logo Design": "$300",
  "Ad Cards & Flyers": "Ad card $35, flyer $75",
  "Social Media Graphics": "$30 each",
  "Business Cards": "$75",
  "Menus & Signage": "$150",
  "Brand Packages": "$800",
};

export const HOURLY: PriceRow = {
  name: "One-off work",
  price: "$75/hr",
  desc: "Edits, fixes, and anything not listed. 1-hour minimum, then 15-minute steps. Unused plan time does not roll over.",
};

export const OTHER_PRICES: PriceRow[] = [
  HOURLY,
  {
    name: "Domain name",
    price: "At cost",
    desc: "What the registrar charges, no markup. You own it.",
  },
];
